import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Search, TrendingUp, AlertTriangle, Clock, Users, UserCheck, Repeat } from 'lucide-react';
import { analyticsApi } from '../../api/endpoints/analytics';
import { StatCard } from '../../components/ui/StatCard';
import { Button } from '../../components/ui/Button';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { formatRelativeTime } from '../../lib/formatters';

type SearchType = 'popular' | 'failed' | 'recent';

export function SearchAnalyticsPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [type, setType] = useState<SearchType>('popular');
  const [days, setDays] = useState(30);

  const { data, isLoading } = useQuery({
    queryKey: ['search-analytics', storeId, type, days],
    queryFn: () => analyticsApi.getSearchAnalytics(storeId!, type, 50, days),
    enabled: !!storeId,
  });

  const searches = data?.searches ?? [];
  const stats = data?.stats;

  if (isLoading) return <LoadingSpinner fullPage />;

  return (
    <div>
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate(`/${storeId}/analytics`)}
          className="rounded-lg p-2 text-text-muted hover:bg-bg-hover hover:text-text-primary"
        >
          <ArrowRight className="h-5 w-5" />
        </button>
        <h1 className="text-xl font-bold">{t('searchAnalytics.title', 'Search Analytics')}</h1>
      </div>

      {/* Days filter */}
      <div className="mb-4 flex gap-2">
        {[7, 30, 90, 365].map(d => (
          <Button
            key={d}
            variant={days === d ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setDays(d)}
          >
            {d === 365 ? t('common.last12Months', 'Last 12 months') : t('searchAnalytics.lastDays', { days: d, defaultValue: `Last ${d} days` })}
          </Button>
        ))}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-4">
          <StatCard
            title={t('searchAnalytics.totalSearches', 'Total Searches')}
            value={stats.totalSearches}
            icon={<Search className="h-5 w-5" />}
          />
          <StatCard
            title={t('searchAnalytics.uniqueTerms', 'Unique Terms')}
            value={stats.uniqueTerms}
            icon={<TrendingUp className="h-5 w-5" />}
          />
          <StatCard
            title={t('searchAnalytics.uniqueVisitors', 'Unique Visitors')}
            value={stats.uniqueVisitors ?? 0}
            icon={<Users className="h-5 w-5" />}
          />
          <StatCard
            title={t('searchAnalytics.uniqueCustomers', 'Logged-in Users')}
            value={stats.uniqueCustomers ?? 0}
            icon={<UserCheck className="h-5 w-5" />}
          />
        </div>
      )}
      {stats && (
        <div className="grid gap-4 sm:grid-cols-3 mb-6">
          <StatCard
            title={t('searchAnalytics.uniqueSessions', 'Unique Sessions')}
            value={stats.uniqueSessions ?? 0}
            icon={<Clock className="h-5 w-5" />}
          />
          <StatCard
            title={t('searchAnalytics.avgSearchesPerSession', 'Avg Searches / Session')}
            value={stats.avgSearchesPerSession ?? 0}
            icon={<Repeat className="h-5 w-5" />}
          />
          <StatCard
            title={t('searchAnalytics.failedSearches', 'Failed Searches')}
            value={stats.failedSearches}
            icon={<AlertTriangle className="h-5 w-5" />}
          />
        </div>
      )}

      {/* Type tabs */}
      <div className="mb-4 flex gap-2">
        {(['popular', 'failed', 'recent'] as const).map((t_) => (
          <Button key={t_} variant={type === t_ ? 'primary' : 'secondary'} size="sm" onClick={() => setType(t_)}>
            {t_ === 'popular' && <TrendingUp className="h-4 w-4" />}
            {t_ === 'failed' && <AlertTriangle className="h-4 w-4" />}
            {t_ === 'recent' && <Clock className="h-4 w-4" />}
            {t(`searchAnalytics.${t_}`, t_ === 'popular' ? 'Popular' : t_ === 'failed' ? 'Failed' : 'Recent')}
          </Button>
        ))}
      </div>

      {/* Table */}
      {searches.length === 0 ? (
        <EmptyState title={t('common.noData')} />
      ) : (
        <div className="rounded-xl border border-border-default bg-bg-secondary overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-default text-text-muted">
                <th className="px-4 py-3 text-start font-medium">{t('searchAnalytics.searchTerm', 'Search Term')}</th>
                <th className="px-4 py-3 text-start font-medium">{t('searchAnalytics.searchCount', 'Searches')}</th>
                <th className="px-4 py-3 text-start font-medium">{t('searchAnalytics.uniqueVisitors', 'Visitors')}</th>
                <th className="px-4 py-3 text-start font-medium">{t('searchAnalytics.uniqueCustomers', 'Users')}</th>
                <th className="px-4 py-3 text-start font-medium">{t('searchAnalytics.uniqueSessions', 'Sessions')}</th>
                <th className="px-4 py-3 text-start font-medium">{t('searchAnalytics.results', 'Results')}</th>
                <th className="px-4 py-3 text-start font-medium">{t('searchAnalytics.lastSearched', 'Last Searched')}</th>
              </tr>
            </thead>
            <tbody>
              {searches.map((s, i) => (
                <tr key={i} className="border-b border-border-default/50 last:border-0 hover:bg-bg-hover transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Search className="h-3.5 w-3.5 text-text-muted shrink-0" />
                      <span className="font-medium">{s.query}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-text-secondary font-medium">{s.count}</td>
                  <td className="px-4 py-3 text-text-secondary">{s.uniqueVisitors ?? 0}</td>
                  <td className="px-4 py-3 text-text-secondary">{s.uniqueCustomers ?? 0}</td>
                  <td className="px-4 py-3 text-text-secondary">{s.uniqueSessions ?? 0}</td>
                  <td className="px-4 py-3">
                    <span className={s.resultCount === 0 ? 'text-danger font-medium' : 'text-text-secondary'}>
                      {s.resultCount}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-text-muted">
                    {formatRelativeTime(s.lastSearched, i18n.language)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
